/*
  ============================================================
  assets/share-page.js
  ============================================================
  Custom Element <share-page>.

  Companion files:
    sections/share-page.liquid
    snippets/share-page.liquid
    snippets/share-page-icon.liquid
    assets/share-page.css

  - Baut die Share-URL zur Laufzeit aus der aktuellen Adresszeile, damit ein
    Variantenwechsel (?variant=…) automatisch mitwandert.
  - Faellt auf die Varianten-ID aus dem Warenkorbformular zurueck, falls das
    Theme die URL nicht schreibt.
  - Entfernt Tracking-Parameter (utm_*, fbclid, gclid, …) aus dem Link.
  - WhatsApp und E-Mail sind echte <a>-Links, deren href aktualisiert wird.
  - Signal hat kein offizielles Share-Schema: Web Share API, sonst Copy.

  Varianten-Events: FoxEcom-Themes (Hyper, Pebble, Sleek, Minimog, Zest)
  feuern 'variant:changed'. 'variant:change' ist die Dawn-Schreibweise.
  Beide werden abgehoert, dazu 'page:loaded'.
  ============================================================
*/

(function () {
  'use strict';

  /* Tracking-Parameter, die nicht mitgeteilt werden sollen */
  var STRIP_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'fbclid',
    'gclid',
    'msclkid',
    'srsltid',
    '_kx',
    'ref'
  ];

  var RESET_DELAY = 2400;

  /*
    Events, nach denen die Share-URL neu gebaut wird.
    FoxEcom-Themes (Hyper, Pebble, Sleek, Minimog, Zest) feuern 'variant:changed'.
    'variant:change' ist die Dawn-Schreibweise, 'page:loaded' fuellt den Fall ab,
    dass das Theme die Buybox nachlaedt.
  */
  var REFRESH_EVENTS = ['variant:changed', 'variant:change', 'page:loaded'];

  /*
    Fallback fuer den Feature-Detect. Idealerweise steht die gleiche Zeile
    bereits inline im <head> (siehe blocks/share-page.liquid), damit die
    Buttons beim Laden nicht umspringen.
  */
  if ('share' in navigator) {
    document.documentElement.classList.add('supports-web-share');
  }

  function SharePageFactory() {
    return class SharePage extends HTMLElement {
      constructor() {
        super();
        this.handleActivate = this.handleActivate.bind(this);
        this.refreshLinks = this.refreshLinks.bind(this);
        this.resetTimer = null;
      }

      connectedCallback() {
        this.statusEl = this.querySelector('[data-share-status]');

        this.removeUnsupported();
        this.refreshLinks();

        this.addEventListener('click', this.handleActivate);
        /* Vor dem eigentlichen Klick nochmal aktualisieren, falls sich die
           Variante zwischenzeitlich geaendert hat. */
        this.addEventListener('pointerdown', this.refreshLinks);
        this.addEventListener('focusin', this.refreshLinks);
        window.addEventListener('popstate', this.refreshLinks);

        var self = this;
        REFRESH_EVENTS.forEach(function (name) {
          document.addEventListener(name, self.refreshLinks);
        });
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.refreshLinks);

        var self = this;
        REFRESH_EVENTS.forEach(function (name) {
          document.removeEventListener(name, self.refreshLinks);
        });
        clearTimeout(this.resetTimer);
      }

      /* ------------------------------------------------------------- URL -- */

      get shareUrl() {
        var base = this.dataset.shareUrl || window.location.href;
        var url;

        try {
          url = new URL(base, window.location.origin);
        } catch (error) {
          return window.location.href;
        }

        /* Query der aktuellen Seite uebernehmen (enthaelt ?variant=…) */
        var current = new URLSearchParams(window.location.search);
        STRIP_PARAMS.forEach(function (key) {
          current.delete(key);
        });
        current.forEach(function (value, key) {
          url.searchParams.set(key, value);
        });

        /* Falls das Theme die URL (noch) nicht geschrieben hat:
           Varianten-ID aus dem Produktformular lesen. */
        if (!url.searchParams.has('variant')) {
          var variantId = this.detectVariantId();
          if (variantId) url.searchParams.set('variant', variantId);
        }

        return url.toString();
      }

      detectVariantId() {
        var input = document.querySelector(
          'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]'
        );
        return input && input.value ? input.value : null;
      }

      /* --------------------------------------------------------- Message -- */

      get message() {
        var raw = this.dataset.shareMessage || '';
        var url = this.shareUrl;

        if (raw.indexOf('[link]') !== -1) {
          return raw.split('[link]').join(url).replace(/\s{2,}/g, ' ').trim();
        }
        return raw ? raw.trim() + ' ' + url : url;
      }

      /* --------------------------------------------------------- Actions -- */

      removeUnsupported() {
        var nativeButton = this.querySelector('[data-share-action="native"]');
        if (nativeButton && !navigator.share) {
          var item = nativeButton.closest('.share-page__item');
          if (item) item.remove();
        }
      }

      refreshLinks() {
        var message = this.message;

        var whatsapp = this.querySelector('[data-share-action="whatsapp"]');
        if (whatsapp) {
          whatsapp.href = 'https://wa.me/?text=' + encodeURIComponent(message);
        }

        var email = this.querySelector('[data-share-action="email"]');
        if (email) {
          email.href =
            'mailto:?subject=' +
            encodeURIComponent(this.dataset.shareSubject || '') +
            '&body=' +
            encodeURIComponent(message);
        }
      }

      handleActivate(event) {
        var trigger = event.target.closest('[data-share-action]');
        if (!trigger || !this.contains(trigger)) return;

        var action = trigger.dataset.shareAction;

        /* whatsapp und email sind echte Links und brauchen kein JS */
        if (action === 'copy') {
          event.preventDefault();
          this.copyToClipboard(trigger);
        } else if (action === 'signal') {
          event.preventDefault();
          this.shareViaSignal(trigger);
        } else if (action === 'native') {
          event.preventDefault();
          this.shareNative();
        }
      }

      /* ----------------------------------------------------------- Copy -- */

      copyToClipboard(button, customMessage) {
        var text =
          this.dataset.shareCopy === 'message' ? this.message : this.shareUrl;
        var self = this;

        this.writeClipboard(text).then(function (ok) {
          if (ok) {
            self.setState(button, 'success');
            self.announce(
              customMessage || self.dataset.shareCopiedText || 'Link kopiert'
            );
          } else {
            self.announce('Kopieren war nicht möglich.');
          }
        });
      }

      writeClipboard(text) {
        var self = this;

        if (navigator.clipboard && window.isSecureContext) {
          return navigator.clipboard
            .writeText(text)
            .then(function () {
              return true;
            })
            .catch(function () {
              return self.legacyCopy(text);
            });
        }
        return Promise.resolve(this.legacyCopy(text));
      }

      legacyCopy(text) {
        var textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();

        var ok = false;
        try {
          ok = document.execCommand('copy');
        } catch (error) {
          ok = false;
        }

        document.body.removeChild(textarea);
        return ok;
      }

      /* --------------------------------------------------------- Signal -- */

      shareViaSignal(button) {
        var message = this.message;

        if (this.dataset.shareSignalDeeplink === 'true') {
          var left = false;
          var onHide = function () {
            left = true;
          };

          document.addEventListener('visibilitychange', onHide, { once: true });
          window.location.href = 'sgnl://send?text=' + encodeURIComponent(message);

          var self = this;
          setTimeout(function () {
            document.removeEventListener('visibilitychange', onHide);
            if (!left && document.visibilityState === 'visible') {
              self.signalFallback(button);
            }
          }, 1200);
          return;
        }

        this.signalFallback(button);
      }

      signalFallback(button) {
        if (navigator.share) {
          this.shareNative();
          return;
        }
        this.copyToClipboard(
          button,
          'Text kopiert – jetzt in Signal einfügen.'
        );
        this.announce('Text kopiert – jetzt in Signal einfügen.');
      }

      /* --------------------------------------------------- Web Share API -- */

      shareNative() {
        if (!navigator.share) return;

        navigator
          .share({
            title: this.dataset.shareTitle || document.title,
            text: this.dataset.shareText || '',
            url: this.shareUrl
          })
          .catch(function () {
            /* Abbruch durch den Nutzer – kein Fehlerzustand */
          });
      }

      /* ------------------------------------------------------------- UI -- */

      setState(button, state) {
        if (!button) return;
        clearTimeout(this.resetTimer);
        button.dataset.state = state;

        this.resetTimer = setTimeout(function () {
          delete button.dataset.state;
        }, RESET_DELAY);
      }

      announce(text) {
        if (!this.statusEl) return;
        this.statusEl.textContent = '';
        var el = this.statusEl;
        /* Kurzes Leeren erzwingt eine neue Ansage im Screenreader */
        setTimeout(function () {
          el.textContent = text;
        }, 60);
      }
    };
  }

  if (!customElements.get('share-page')) {
    customElements.define('share-page', SharePageFactory());
  }
})();